import React, { useState, useEffect } from 'react';
import { cancelCallByToken, acceptCallByToken, declineCallByToken, getCallByToken } from '../../../api/v2/calls';
import { formatScheduledAt } from '../../../helpers/timezone_utils';

import { PhoneIcon } from '../call_page_icon';

import styles from './index.module.css';

const ACTION_CONFIG = {
  cancel: {
    title: (name) => (name ? `Cancel Your Call with ${name}` : 'Cancel Your Call'),
    description: 'Are you sure you want to cancel this call request? This action cannot be undone.',
    actionLabel: 'Cancel the Call',
    confirmLabel: 'Yes, Cancel the Call',
    confirmStyle: styles.btn_danger,
    successTitle: 'Call Canceled',
    successMessage: 'Your call has been successfully canceled.',
    errorDefault: 'This call cannot be canceled. It may have already been canceled or completed.',
    isAvailable: (info) => info?.cancelable,
    unavailableMessage: (info) =>
      `This call can no longer be canceled, it is currently ${info?.status?.replace(/_/g, ' ')}.`,
    apiCall: cancelCallByToken,
  },
  accept: {
    title: (name) => (name ? `Accept Call from ${name}` : 'Accept This Call'),
    description: 'Would you like to accept this call request?',
    actionLabel: 'Accept the Call',
    confirmLabel: 'Yes, Accept the Call',
    confirmStyle: styles.btn_success,
    successTitle: 'Call Accepted',
    successMessage: "You've accepted the call request. You'll receive a confirmation shortly.",
    errorDefault: 'This call request can no longer be accepted. It may have expired or already been processed.',
    isAvailable: (info) => info?.actionable,
    unavailableMessage: (info) =>
      `This call request can no longer be accepted, it is currently ${info?.status?.replace(/_/g, ' ')}.`,
    apiCall: acceptCallByToken,
  },
  decline: {
    title: (name) => (name ? `Decline Call from ${name}` : 'Decline This Call'),
    description: 'Would you like to decline this call request?',
    actionLabel: 'Decline the Call',
    confirmLabel: 'Yes, Decline the Call',
    confirmStyle: styles.btn_danger,
    successTitle: 'Call Declined',
    successMessage: "You've declined the call request.",
    errorDefault: 'This call request can no longer be declined. It may have expired or already been processed.',
    isAvailable: (info) => info?.actionable,
    unavailableMessage: (info) =>
      `This call request can no longer be declined, it is currently ${info?.status?.replace(/_/g, ' ')}.`,
    apiCall: declineCallByToken,
  },
};

const CallDetails = ({ callInfo }) => {
  const { other_user_name, scheduled, scheduled_at } = callInfo;

  return (
    <div className={styles.call_details}>
      {other_user_name && (
        <div className={styles.detail_row}>
          <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' className={styles.detail_icon}>
            <circle cx='12' cy='8' r='4' stroke='#3eb0bb' strokeWidth='1.75' />
            <path d='M4 20c0-4 3.582-7 8-7s8 3 8 7' stroke='#3eb0bb' strokeWidth='1.75' strokeLinecap='round' />
          </svg>
          <span>{other_user_name}</span>
        </div>
      )}
      <div className={styles.detail_row}>
        {scheduled && scheduled_at ? (
          <>
            <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' className={styles.detail_icon}>
              <rect x='3' y='4' width='18' height='17' rx='2' stroke='#3eb0bb' strokeWidth='1.75' />
              <path d='M3 9h18M8 2v4M16 2v4' stroke='#3eb0bb' strokeWidth='1.75' strokeLinecap='round' />
            </svg>
            <span>{formatScheduledAt(scheduled_at)}</span>
          </>
        ) : (
          <>
            <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' className={styles.detail_icon}>
              <path
                d='M20.9995 16.4767V19.1864C21.0037 20.2223 20.0723 21.0873 19.0265 20.9929C10.0001 21 3.00006 13.935 3.00713 4.96919C2.91294 3.92895 3.77364 3.00106 4.80817 3.00009H7.52331C7.96253 2.99577 8.38835 3.151 8.72138 3.43684C9.66819 4.24949 10.2772 7.00777 10.0429 8.10428C9.85994 8.96036 8.99696 9.55929 8.41026 10.1448C9.69864 12.4062 11.5747 14.2785 13.8405 15.5644C14.4272 14.9788 15.0274 14.1176 15.8851 13.935C16.9855 13.7008 19.7615 14.3106 20.5709 15.264C20.858 15.6021 21.0105 16.0337 20.9995 16.4767Z'
                stroke='#3eb0bb'
                strokeWidth='1.75'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            <span>Immediate call</span>
          </>
        )}
      </div>
    </div>
  );
};

const CallTokenConfirmation = ({ match }) => {
  const { action, token } = match.params;
  const [callInfo, setCallInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [actionStatus, setActionStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [confirming, setConfirming] = useState(false);

  const config = ACTION_CONFIG[action];

  useEffect(() => {
    if (!config) return;
    getCallByToken(token)
      .then((data) => setCallInfo(data))
      .catch(() => setCallInfo(false))
      .finally(() => setInfoLoading(false));
  }, [token, config]);

  if (!config) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.icon} aria-hidden='true'>
            <PhoneIcon />
          </div>
          <p className={styles.error_message}>Invalid link. Please check the URL and try again.</p>
        </div>
      </div>
    );
  }

  const handleConfirm = async () => {
    setActionStatus('loading');
    try {
      await config.apiCall(token);
      setActionStatus('success');
    } catch (err) {
      setActionStatus('error');
      setErrorMessage(err.message || config.errorDefault);
    }
  };

  if (actionStatus === 'success') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.icon} aria-hidden='true'>
            <PhoneIcon />
          </div>
          <h1 className={styles.title}>{config.successTitle}</h1>
          <p className={styles.description}>{config.successMessage}</p>
          <a href='/member/conversations' className={styles.back_link}>
            Go to your conversations
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.icon} aria-hidden='true'>
          <PhoneIcon />
        </div>
        {infoLoading ? (
          <div className={styles.loading}>
            <div className={styles.skeleton_title} />
            <div className={styles.skeleton_row} />
            <div className={styles.skeleton_row} style={{ width: '60%' }} />
          </div>
        ) : (
          <>
            <h1 className={styles.title}>{config.title(callInfo?.other_user_name)}</h1>
            {callInfo && <CallDetails callInfo={callInfo} />}
            {callInfo !== false && !config.isAvailable(callInfo) ? (
              <div className={styles.already_processed}>{config.unavailableMessage(callInfo)}</div>
            ) : confirming ? (
              <>
                <p className={styles.description}>{config.description}</p>
                {actionStatus === 'error' && <div className={styles.error_box}>{errorMessage}</div>}
                <button
                  type='button'
                  className={`${styles.btn} ${config.confirmStyle}`}
                  onClick={handleConfirm}
                  disabled={actionStatus === 'loading'}
                >
                  {actionStatus === 'loading' ? 'Processing...' : config.confirmLabel}
                </button>
                <button
                  type='button'
                  className={`${styles.btn} ${styles.btn_secondary}`}
                  onClick={() => setConfirming(false)}
                  disabled={actionStatus === 'loading'}
                >
                  Go back
                </button>
              </>
            ) : (
              <>
                <p className={styles.description}>{config.description}</p>
                <button
                  type='button'
                  className={`${styles.btn} ${config.confirmStyle}`}
                  onClick={() => setConfirming(true)}
                >
                  {config.actionLabel}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CallTokenConfirmation;
